import {
  BillingAddress,
  OrderAddress,
  ShippingAddress,
} from '__generated__/atlas-types'

import {
  BillingAddressFormValues,
  FieldIds as BillingFieldsIds,
} from '../components/Overview/components/AddressesWidget/components/Forms/BillingAddressForm/billingAddressForm.types'
import {
  ShippingAddressFormValues,
  FieldIds as ShippingFieldIds,
} from '../components/Overview/components/AddressesWidget/components/Forms/ShippingAddressForm/shippingAddressForm.types'
import { ProvidedAddress } from '../components/Overview/components/AddressesWidget/components/WarningModals/UpdateAddressMismatchModal'

export const formatOrderAddress = (
  orderAddress: OrderAddress | undefined,
  separator: string = ', ',
) => {
  if (!orderAddress) {
    return ''
  }

  const { firstAddress, secondAddress, city, state, country, zip } =
    orderAddress

  return [firstAddress, secondAddress, city, state, country, zip]
    .filter(Boolean)
    .join(separator)
}

export const isSameShippingAddressThanValues = (
  shippingAddress: ShippingAddress,
  values: ShippingAddressFormValues,
) => {
  return (
    values[ShippingFieldIds.CompanyName].trim().toLowerCase() ===
      shippingAddress.address?.companyName?.trim().toLowerCase() &&
    values[ShippingFieldIds.Address1].trim().toLowerCase() ===
      shippingAddress.address?.line1?.trim().toLowerCase() &&
    values[ShippingFieldIds.Address2].trim().toLowerCase() ===
      shippingAddress.address?.line2?.trim().toLowerCase() &&
    values[ShippingFieldIds.City].trim().toLowerCase() ===
      shippingAddress.address?.city?.trim().toLowerCase() &&
    values[ShippingFieldIds.Country]?.label.trim().toLowerCase() ===
      shippingAddress.address?.country?.trim().toLowerCase() &&
    values[ShippingFieldIds.PostalCode].trim().toLowerCase() ===
      shippingAddress.address?.zip?.trim().toLowerCase() &&
    values[ShippingFieldIds.Territory].trim().toLowerCase() ===
      shippingAddress.address?.state?.trim().toLowerCase() &&
    values[ShippingFieldIds.Phone].trim().toLowerCase() ===
      shippingAddress.phoneNumber?.trim().toLowerCase() &&
    values[ShippingFieldIds.Email]?.trim().toLowerCase() ===
      shippingAddress.email?.trim().toLowerCase()
  )
}

export const isSameBillingAddressThanValues = (
  billingAddress: BillingAddress,
  values: BillingAddressFormValues,
) => {
  return (
    values[BillingFieldsIds.CompanyName].trim().toLowerCase() ===
      billingAddress.address?.companyName?.trim().toLowerCase() &&
    values[BillingFieldsIds.Address1].trim().toLowerCase() ===
      billingAddress.address?.line1?.trim().toLowerCase() &&
    values[BillingFieldsIds.Address2].trim().toLowerCase() ===
      billingAddress.address?.line2?.trim().toLowerCase() &&
    values[BillingFieldsIds.City].trim().toLowerCase() ===
      billingAddress.address?.city?.trim().toLowerCase() &&
    values[BillingFieldsIds.Country]?.label.trim().toLowerCase() ===
      billingAddress.address?.country?.trim().toLowerCase() &&
    values[BillingFieldsIds.PostalCode].trim().toLowerCase() ===
      billingAddress.address?.zip?.trim().toLowerCase() &&
    values[BillingFieldsIds.Territory].trim().toLowerCase() ===
      billingAddress.address?.state?.trim().toLowerCase() &&
    values[BillingFieldsIds.Phone].trim().toLowerCase() ===
      billingAddress.phoneNumber?.trim().toLowerCase()
  )
}

export const formatShippingAddressToProvidedAddress = (
  address: ShippingAddress,
): ProvidedAddress => ({
  companyName: address.address?.companyName,
  addressFull: formatOrderAddress({
    city: address.address?.city,
    country: address.address?.country,
    firstAddress: address.address?.line1,
    secondAddress: address.address?.line2,
    state: address.address?.state,
    zip: address.address?.zip,
  }),
  phoneNumber: address.phoneNumber,
  email: address?.email,
})

export const formatBillingAddressToProvidedAddress = (
  address: BillingAddress,
): ProvidedAddress => ({
  companyName: address.address?.companyName,
  addressFull: formatOrderAddress({
    city: address.address?.city,
    country: address.address?.country,
    firstAddress: address.address?.line1,
    secondAddress: address.address?.line2,
    state: address.address?.state,
    zip: address.address?.zip,
  }),
  phoneNumber: address.phoneNumber,
})

export const formatShippingAddressValuesToProvidedAddress = (
  values: ShippingAddressFormValues,
): ProvidedAddress => ({
  companyName: values[ShippingFieldIds.CompanyName],
  addressFull: formatOrderAddress({
    city: values[ShippingFieldIds.City],
    country: values[ShippingFieldIds.Country]?.label,
    firstAddress: values[ShippingFieldIds.Address1],
    secondAddress: values[ShippingFieldIds.Address2],
    state: values[ShippingFieldIds.Territory],
    zip: values[ShippingFieldIds.PostalCode],
  }),
  phoneNumber: values[ShippingFieldIds.Phone],
  email: values[ShippingFieldIds.Email],
})

export const formatBillingAddressValuesToProvidedAddress = (
  values: BillingAddressFormValues,
): ProvidedAddress => ({
  companyName: values[BillingFieldsIds.CompanyName],
  addressFull: formatOrderAddress({
    city: values[BillingFieldsIds.City],
    country: values[BillingFieldsIds.Country]?.label,
    firstAddress: values[BillingFieldsIds.Address1],
    secondAddress: values[BillingFieldsIds.Address2],
    state: values[BillingFieldsIds.Territory],
    zip: values[BillingFieldsIds.PostalCode],
  }),
  phoneNumber: values[BillingFieldsIds.Phone],
})
